/**
 * Route table.
 *
 * Extracted from App.svelte so it can be asserted on as plain data by
 * tests/unit/routes.test.js.
 *
 * ORDER IN THIS ARRAY DOES NOT MATTER. That is worth stating plainly, because
 * the comments here previously claimed the opposite — inherited from
 * plugins/augurly/svelte/src/App.svelte and never checked. The router sorts by
 * `RouterPathPriority` (template length descending) before matching, so the
 * most specific template always wins no matter how these are listed.
 *
 * Matching IS by prefix — the router's default is non-strict and the regex is
 * `^${template}` with no end anchor, so '/settings' really does match
 * '/settings/tenant'. The priority sort is what makes that harmless.
 *
 * The real hazard is two templates of EQUAL length (say '/review/new' beside
 * '/review/:id'), where the tie breaks on a localeCompare nobody would predict.
 * The test resolves every route through the library's own Matcher and priority
 * function and asserts each one reaches itself.
 *
 * Route components are STATICALLY imported. Do not convert these to
 * `() => import(...)` — read the comment block in App.svelte first.
 */

import Login from './routes/Login.svelte';
import MagicLinkVerify from './routes/MagicLinkVerify.svelte';
import Register from './routes/Register.svelte';
import EmailVerify from './routes/EmailVerify.svelte';
import Properties from './routes/Properties.svelte';
import PropertyDetail from './routes/PropertyDetail.svelte';
import Conversations from './routes/Conversations.svelte';
import ConversationDetail from './routes/ConversationDetail.svelte';
import Reservations from './routes/Reservations.svelte';
import TenantSettings from './routes/TenantSettings.svelte';
import Settings from './routes/Settings.svelte';
import Review from './routes/Review.svelte';
import ReviewDetail from './routes/ReviewDetail.svelte';
import Unlinked from './routes/Unlinked.svelte';
import Pipeline from './routes/Pipeline.svelte';
import Situations from './routes/Situations.svelte';
import Rules from './routes/Rules.svelte';
import AdminUsers from './routes/admin/Users.svelte';
import AdminAudit from './routes/admin/Audit.svelte';
import AdminTenants from './routes/admin/Tenants.svelte';
import AdminTenantDetail from './routes/admin/TenantDetail.svelte';
import AdminTestChat from './routes/admin/TestChat.svelte';
import Dashboard from './routes/Dashboard.svelte';
import { guardRoute } from './lib/routeGuard.js';

export const BASE = '/strao';

/** The roles the server recognises. Kept in sync with plugins/strao_portal/scope.py. */
export const ROLES = ['super_user', 'pm_staff', 'property_owner'];

/**
 * Attach the admission guard to every route that requires auth.
 *
 * Done programmatically rather than by writing `beforeEnter` on each entry, so
 * adding a protected route cannot silently ship without a guard — which is the
 * exact failure mode that left meta.requiresRole decorative for the whole
 * build. tests/unit/routes.test.js asserts the coverage holds.
 */
function withGuards(defs) {
  return defs.map((route) =>
    route.meta?.requiresAuth ? { ...route, beforeEnter: guardRoute } : route,
  );
}

export const routes = withGuards([
  // Public
  { name: 'login', path: '/login', component: Login,
    title: 'Sign in · STRAO Portal' },
  { name: 'magic-link-verify', path: '/magic-link-verify', component: MagicLinkVerify,
    title: 'Signing in · STRAO Portal' },
  { name: 'register', path: '/register', component: Register,
    title: 'Create your account · STRAO Portal' },
  { name: 'verify-email', path: '/verify-email', component: EmailVerify,
    title: 'Confirming your email · STRAO Portal' },

  // Authenticated
  { name: 'properties', path: '/properties', component: Properties,
    title: 'Properties · STRAO Portal', meta: { requiresAuth: true } },
  { name: 'property-detail', path: '/properties/:id', component: PropertyDetail,
    title: 'Property · STRAO Portal', meta: { requiresAuth: true } },
  { name: 'conversations', path: '/conversations', component: Conversations,
    title: 'Conversations · STRAO Portal', meta: { requiresAuth: true } },
  { name: 'conversation-detail', path: '/conversations/:id', component: ConversationDetail,
    title: 'Conversation · STRAO Portal', meta: { requiresAuth: true } },
  { name: 'reservations', path: '/reservations', component: Reservations,
    title: 'Reservations · STRAO Portal', meta: { requiresAuth: true } },
  { name: 'tenant-settings', path: '/settings/tenant', component: TenantSettings,
    title: 'Auto-responder settings · STRAO Portal',
    meta: { requiresAuth: true, requiresRole: ['super_user', 'pm_staff'] } },
  { name: 'settings', path: '/settings', component: Settings,
    title: 'Settings · STRAO Portal', meta: { requiresAuth: true } },

  // Staff only (super_user + pm_staff). Reviewing is the property manager's
  // job, and approve/override send a message to the guest — the server
  // enforces the same rule with require_staff on every review endpoint.
  { name: 'review', path: '/review', component: Review,
    title: 'Review queue · STRAO Portal',
    meta: { requiresAuth: true, requiresRole: ['super_user', 'pm_staff'] } },
  { name: 'review-detail', path: '/review/:id', component: ReviewDetail,
    title: 'Review item · STRAO Portal',
    meta: { requiresAuth: true, requiresRole: ['super_user', 'pm_staff'] } },

  { name: 'unlinked', path: '/unlinked', component: Unlinked,
    title: 'Unlinked records · STRAO Portal',
    meta: { requiresAuth: true, requiresRole: ['super_user', 'pm_staff'] } },

  // User-facing label is "Saved replies", not "Situations" — the manager's
  // mental model is "the answer I always give", not a taxonomy. The path and
  // route name stay aligned with the API for greppability.
  { name: 'situations', path: '/situations', component: Situations,
    title: 'Saved replies · STRAO Portal',
    meta: { requiresAuth: true, requiresRole: ['super_user', 'pm_staff'] } },

  // Facts and policies true for every property (T1). Owners never see these.
  { name: 'rules', path: '/rules', component: Rules,
    title: 'Rules for all properties · STRAO Portal',
    meta: { requiresAuth: true, requiresRole: ['super_user', 'pm_staff'] } },

  { name: 'pipeline', path: '/pipeline', component: Pipeline,
    title: 'Pipeline health · STRAO Portal',
    meta: { requiresAuth: true, requiresRole: ['super_user', 'pm_staff'] } },

  // Super-user only. The client gate is UX; the server enforces the same rule
  // via require_super_user on every one of these endpoints.
  { name: 'admin-users', path: '/admin/users', component: AdminUsers,
    title: 'People & access · STRAO Portal',
    meta: { requiresAuth: true, requiresRole: 'super_user' } },
  { name: 'admin-audit', path: '/admin/audit', component: AdminAudit,
    title: 'Audit log · STRAO Portal',
    meta: { requiresAuth: true, requiresRole: 'super_user' } },
  { name: 'admin-tenants', path: '/admin/tenants', component: AdminTenants,
    title: 'Tenants · STRAO Portal',
    meta: { requiresAuth: true, requiresRole: 'super_user' } },
  { name: 'admin-tenant-detail', path: '/admin/tenants/:id', component: AdminTenantDetail,
    title: 'Tenant · STRAO Portal',
    meta: { requiresAuth: true, requiresRole: 'super_user' } },
  // Play a guest and see what the auto-responder would do. Nothing is sent.
  { name: 'admin-test-chat', path: '/admin/test-chat', component: AdminTestChat,
    title: 'Test chat · STRAO Portal',
    meta: { requiresAuth: true, requiresRole: 'super_user' } },

  // '/' prefix-matches everything, but it is also the shortest template, so the
  // priority sort puts it last automatically. Listed last for readability only.
  { name: 'dashboard', path: '/', component: Dashboard,
    title: 'Dashboard · STRAO Portal', meta: { requiresAuth: true } },
]);

export const routerOptions = { routes, base: BASE };
